import { Info } from "lucide-react";
import { useEffect, useState } from "react";

import { Input } from "./input";
import { InputStyles } from "./input-styles";
import { BasicTooltip } from "./Tooltip";
import { cn } from "./utils";

export interface URLInputProps {
  id?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  placeholder?: string;
  label?: string;
  tooltip?: string;
  readOnly?: boolean;
  error?: string;
  showError?: boolean;
  className?: string;
  classNameInput?: string;
  classNamePrefix?: string;
}

export const URLInput = ({
  id,
  value = "",
  onChange,
  onBlur,
  placeholder,
  label,
  tooltip,
  readOnly = false,
  error,
  showError = false,
  className,
  classNameInput,
  classNamePrefix,
}: URLInputProps) => {
  const [displayValue, setDisplayValue] = useState("");

  useEffect(() => {
    if (value) {
      setDisplayValue(value.startsWith("https://") ? value.substring(8) : value);
    } else {
      setDisplayValue("");
    }
  }, [value]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setDisplayValue(inputValue);

    const fullValue =
      inputValue.startsWith("https://") || inputValue.startsWith("http://")
        ? inputValue
        : `https://${inputValue}`;
    onChange?.(fullValue);
  };

  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    let finalValue: string;

    if (inputValue.startsWith("https://")) {
      setDisplayValue(inputValue.substring(8));
      finalValue = inputValue;
    } else {
      finalValue = inputValue ? `https://${inputValue}` : "";
    }

    onBlur?.(finalValue);
  };

  return (
    <div className={className}>
      {label ? (
        <div className="flex items-center gap-2">
          <label className="text-md font-medium text-gray-700" htmlFor={id}>
            {label}
          </label>
          {tooltip ? (
            <BasicTooltip content={tooltip}>
              <Info className="h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700" />
            </BasicTooltip>
          ) : null}
        </div>
      ) : null}
      <div className="flex items-center">
        <div
          className={cn(
            "h-9 w-fit items-center justify-center rounded-l-md rounded-r-none border border-r-0 border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500",
            classNamePrefix,
          )}
        >
          https://
        </div>
        <Input
          id={id}
          onChange={onInputChange}
          readOnly={readOnly}
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          value={displayValue}
          onBlur={onInputBlur}
          className={cn(InputStyles.Base, InputStyles.Input, "rounded-l-none", classNameInput)}
          placeholder={placeholder}
        />
      </div>
      {error && showError ? <p className="text-sm text-red-500 ">{error}</p> : null}
    </div>
  );
};
